import { z } from "zod";
export const RBIM_QUESTIONNAIRE_PAYLOAD_SCHEMA_VERSION = 1;
const answerTextSchema = z.string().trim().max(1_000);
const requiredAnswerTextSchema = answerTextSchema.min(1);
const optionalMetadataTextSchema = answerTextSchema.optional();
const calendarDateSchema = answerTextSchema.refine((value) => value === "" || parseMmDdYyyy(value) !== null, "Enter a valid date in MM/DD/YYYY format.");
const householdInformationV1Schema = z
    .object({
    province: requiredAnswerTextSchema,
    provinceCode: answerTextSchema,
    municipality: requiredAnswerTextSchema,
    municipalityCode: answerTextSchema,
    barangay: requiredAnswerTextSchema,
    barangayCode: answerTextSchema,
    purok: answerTextSchema,
    address: requiredAnswerTextSchema,
    householdCode: requiredAnswerTextSchema,
    family: z.enum(["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"]),
    householdType: z.enum(["Household", "Institutional Living Quarter"]),
    respondentName: requiredAnswerTextSchema,
    householdHead: requiredAnswerTextSchema,
    totalMembers: z
        .string()
        .trim()
        .regex(/^0*[1-9]\d{0,2}$/, "Total members must be a positive whole number."),
})
    .strict();
const householdQuestionsV1Schema = z
    .object({
    housingTenure: answerTextSchema,
    lotTenure: answerTextSchema,
    hazardProneArea: answerTextSchema,
    disasterExperiencedPastFiveYears: answerTextSchema,
    knowsNearestEvacuationCenter: answerTextSchema,
    lightingFuel: answerTextSchema,
    cookingFuel: answerTextSchema,
    drinkingWaterSource: answerTextSchema,
    garbageDisposal: answerTextSchema,
    segregatesGarbage: answerTextSchema,
    toiletFacility: answerTextSchema,
    buildingType: answerTextSchema,
    wallMaterial: answerTextSchema,
    femaleDeathPast12Months: answerTextSchema,
    femaleDeathAge: answerTextSchema,
    femaleDeathCause: answerTextSchema,
    childDeathPast12Months: answerTextSchema,
    childDeathAge: answerTextSchema,
    childDeathSex: answerTextSchema,
    childDeathCause: answerTextSchema,
    commonDiseaseCause1: answerTextSchema,
    commonDiseaseCause2: answerTextSchema,
    commonDiseaseCause3: answerTextSchema,
    primaryNeed1: answerTextSchema,
    primaryNeed2: answerTextSchema,
    primaryNeed3: answerTextSchema,
    futureBarangay: answerTextSchema,
    futureMunicipality: answerTextSchema,
    futureProvince: answerTextSchema,
})
    .strict();
const interviewInformationV1Schema = z
    .object({
    visit: requiredAnswerTextSchema,
    dateOfVisit: calendarDateSchema,
    timeStart: answerTextSchema,
    timeEnd: answerTextSchema,
    result: z.enum(["", "C", "CB", "R"]),
    dateOfNextVisit: calendarDateSchema,
    interviewer: answerTextSchema,
    supervisor: answerTextSchema,
})
    .strict();
const encodingInformationV1Schema = z
    .object({
    dateEncoded: calendarDateSchema,
    encoder: answerTextSchema,
    supervisor: answerTextSchema,
})
    .strict();
const householdMemberV1Schema = z
    .object({
    firstName: requiredAnswerTextSchema,
    middleName: answerTextSchema,
    lastName: requiredAnswerTextSchema,
    sex: requiredAnswerTextSchema,
    dateOfBirth: calendarDateSchema,
    birthdatePrecision: z.enum(["exact", "month-year", "year-only", "age-only", "unknown"]),
    birthdateOriginalValue: optionalMetadataTextSchema,
    birthYear: z.number().int().min(1900).nullable().optional(),
    birthMonth: z.number().int().min(1).max(12).nullable().optional(),
    birthDay: z.number().int().min(1).max(31).nullable().optional(),
    importedAge: z.number().int().min(0).max(120).nullable().optional(),
    importedAgeAsOf: answerTextSchema.nullable().optional(),
    birthdateSource: z.enum(["manual", "legacy-import"]).optional(),
    dateOfBirthUnknown: z.boolean(),
    reportedAge: z.number().int().min(0).max(120).nullable(),
    ageAsOfDate: answerTextSchema.nullable(),
    ageSource: z.enum(["date-of-birth", "reported"]).nullable(),
    relationshipToHead: requiredAnswerTextSchema,
    familyUnitAssignment: z.enum(["same", "new", "existing"]),
    familyUnitId: answerTextSchema,
    familyUnitNo: answerTextSchema,
    familyName: answerTextSchema,
    relationshipToFamilyHead: answerTextSchema,
    isFamilyHead: z.boolean(),
    civilStatus: answerTextSchema,
    placeOfBirth: requiredAnswerTextSchema,
    nationality: requiredAnswerTextSchema,
    nationalityOther: answerTextSchema,
    religion: answerTextSchema,
    ethnicity: answerTextSchema,
    highestEducation: answerTextSchema,
    currentlyEnrolled: answerTextSchema,
    schoolLevel: answerTextSchema,
    placeOfSchool: answerTextSchema,
    reasonNotAttendingSchool: answerTextSchema,
    reasonNotAttendingSchoolOther: answerTextSchema,
    monthlyIncome: answerTextSchema,
    sourceOfIncome: answerTextSchema,
    sourceOfIncomeOther: answerTextSchema,
    workStatus: answerTextSchema,
    placeOfWork: answerTextSchema,
    placeOfDelivery: answerTextSchema,
    placeOfDeliveryOther: answerTextSchema,
    birthAttendant: answerTextSchema,
    birthAttendantOther: answerTextSchema,
    immunization: answerTextSchema,
    livingChildren: answerTextSchema,
    livingChildrenAlive: answerTextSchema,
    familyPlanningUse: answerTextSchema,
    familyPlanningUseOther: answerTextSchema,
    fpMethodSource: answerTextSchema,
    fpMethodSourceOther: answerTextSchema,
    intentionToUseFp: answerTextSchema,
    intentionToUseFpDetail: answerTextSchema,
    healthInsurance: answerTextSchema,
    healthInsuranceOther: answerTextSchema,
    facilityVisitedPast12Months: answerTextSchema,
    facilityVisitedPast12MonthsOther: answerTextSchema,
    healthFacilityVisitReason: answerTextSchema,
    healthFacilityVisitReasonOther: answerTextSchema,
    disability: answerTextSchema,
    soloParentStatus: answerTextSchema,
    registeredSeniorCitizen: answerTextSchema,
    registeredVoterBarangay: answerTextSchema,
    previousResidenceFiveYears: answerTextSchema,
    previousResidenceSixMonths: answerTextSchema,
    lengthOfStayInBarangay: answerTextSchema,
    residentType: answerTextSchema,
    dateOfTransfer: answerTextSchema,
    reasonLeavingPreviousResidence1: answerTextSchema,
    reasonLeavingPreviousResidence1Other: answerTextSchema,
    reasonLeavingPreviousResidence2: answerTextSchema,
    reasonLeavingPreviousResidence2Other: answerTextSchema,
    reasonLeavingPreviousResidence3: answerTextSchema,
    reasonLeavingPreviousResidence3Other: answerTextSchema,
    returnToPreviousResidence: answerTextSchema,
    returnToPreviousResidenceDetail: answerTextSchema,
    transferReason1: answerTextSchema,
    transferReason1Other: answerTextSchema,
    transferReason2: answerTextSchema,
    transferReason2Other: answerTextSchema,
    transferReason3: answerTextSchema,
    transferReason3Other: answerTextSchema,
    durationOfStayCurrentBarangay: answerTextSchema,
    durationOfStayCurrentBarangayDetail: answerTextSchema,
    hasValidCtc: answerTextSchema,
    ctcIssuedInBarangay: answerTextSchema,
    skillsTrainingInterest: answerTextSchema,
    skills: answerTextSchema,
    skillsOther: answerTextSchema,
    occupation: answerTextSchema,
})
    .strict()
    .superRefine((member, context) => {
    if (member.dateOfBirthUnknown) {
        if (member.dateOfBirth !== "" || member.ageSource === "date-of-birth") {
            context.addIssue({
                code: "custom",
                path: ["dateOfBirth"],
                message: "Date of birth must be blank when it is marked unknown.",
            });
        }
        if (member.reportedAge === null || member.ageSource !== "reported") {
            context.addIssue({
                code: "custom",
                path: ["reportedAge"],
                message: "A reported age is required when date of birth is unknown.",
            });
        }
        return;
    }
    const birthdate = parseMmDdYyyy(member.dateOfBirth);
    if (!birthdate || birthdate > startOfToday()) {
        context.addIssue({
            code: "custom",
            path: ["dateOfBirth"],
            message: "A valid, non-future date of birth is required.",
        });
    }
    if (member.reportedAge !== null || member.ageSource === "reported") {
        context.addIssue({
            code: "custom",
            path: ["reportedAge"],
            message: "Reported age must be blank when date of birth is known.",
        });
    }
});
export const rbimQuestionnairePayloadV1Schema = z
    .object({
    household: householdInformationV1Schema,
    householdQuestions: householdQuestionsV1Schema,
    interviews: z.tuple([interviewInformationV1Schema, interviewInformationV1Schema]),
    encoding: encodingInformationV1Schema,
    members: z.array(householdMemberV1Schema).min(1).max(999),
})
    .strict()
    .superRefine((payload, context) => {
    const declaredMemberCount = Number(payload.household.totalMembers);
    if (declaredMemberCount !== payload.members.length) {
        context.addIssue({
            code: "custom",
            path: ["household", "totalMembers"],
            message: "Total members must match the number of household members.",
        });
    }
    const headIndexes = payload.members.flatMap((member, index) => isHouseholdHeadAnswer(member.relationshipToHead) ? [index] : []);
    if (headIndexes.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["members"],
            message: "One household head is required.",
        });
    }
    if (headIndexes.length > 1) {
        for (const index of headIndexes) {
            context.addIssue({
                code: "custom",
                path: ["members", index, "relationshipToHead"],
                message: "Only one household head is allowed.",
            });
        }
    }
});
export const createWebPortalRecordRequestSchema = z
    .object({
    barangayId: z.uuid(),
    payloadSchemaVersion: z.literal(RBIM_QUESTIONNAIRE_PAYLOAD_SCHEMA_VERSION),
    payload: rbimQuestionnairePayloadV1Schema,
})
    .strict();
function parseMmDdYyyy(value) {
    const match = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/.exec(value);
    if (!match)
        return null;
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (year < 1900 ||
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day) {
        return null;
    }
    return parsed;
}
function startOfToday() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}
function isHouseholdHeadAnswer(value) {
    const normalized = value.trim().toUpperCase();
    return /^(?:0*1(?:\s*[-:]\s*HEAD)?|HEAD)$/.test(normalized);
}
//# sourceMappingURL=rbim-questionnaire.js.map